import {
    pickAppCopy,
    getTitleSelectOptions,
    formatTitle as formatTitleLabel,
    type TitleSelectOption,
    type SupportedLocale,
} from "@patient-watch/i18n/core";
import { useMemo } from "react";
import { useAppLocale } from "./LocaleContext";
import { profileCopyEn } from "@patient-watch/i18n/webapp/en-GB/profileCopy";
import { profileCopyDe } from "@patient-watch/i18n/webapp/de-DE/profileCopy";
import { profileCopyDa } from "@patient-watch/i18n/webapp/da-DK/profileCopy";
import { profileCopyEnUs } from "@patient-watch/i18n/webapp/en-US/profileCopy";
import { profileCopyFr } from "@patient-watch/i18n/webapp/fr-FR/profileCopy";
import { profileCopyIt } from "@patient-watch/i18n/webapp/it-IT/profileCopy";
import { profileCopyEs } from "@patient-watch/i18n/webapp/es-ES/profileCopy";

interface ProfileCopyBase {
    title: string;
    description: string;
    contactDetails: string;
    name: string;
    titleLabel: string;
    /** Generic dropdown placeholder, e.g. for the title select. */
    selectPlaceholder: string;
    /** Validation message shown when a required field is empty. */
    requiredField: string;
    firstNameRequired: string;
    lastNameRequired: string;
    firstName: string;
    lastName: string;
    noName: string;
    dateOfBirth: string;
    dateOfBirthFormatDescription: string;
    /** Input placeholders for the day/month/year date-of-birth fields. */
    dobPlaceholderDay: string;
    dobPlaceholderMonth: string;
    dobPlaceholderYear: string;
    /** Validation messages for the date-of-birth step. */
    dobDayRequired: string;
    dobMonthRequired: string;
    dobYearRequired: string;
    dobMaxDaysInMonth: string;
    dobMonthsInYear: string;
    dobYearTooEarly: string;
    dobYearInFuture: string;
    noDateOfBirth: string;
    email: string;
    noEmail: string;
    updateEmail: string;
    validEmailAddress: string;
    accountSignInEmailRequired: string;
    newEmailDifferentFromSignIn: string;
    newEmailDifferentFromCurrent: string;
    differentEmailAddress: string;
    noSignInEmailToChange: string;
    emailUpdatedSuccessfully: string;
    emailUpdateFailed: string;
    emailSavedToProfile: string;
    emailSaveToProfileFailed: string;
    loading: string;
    profileEmailIntro: (email: string) => string;
    profileEmailIntroSuffix: string;
    signInEmailDiffersHelp: string;
    noProfileEmailReplaceDescription: string;
    signInEmailOnlyIfDifferent: string;
    signInEmail: string;
    leaveBlankIfSameAsProfile: string;
    newEmail: string;
    emailAddressToChangeTo: string;
    accountSignInEmailIntro: (email: string) => string;
    saveEmailToProfile: string;
    saveEmailFirstDescription: string;
    noProfileEmailAddDescription: string;
    profileEmailLink: string;
    noEmailOnAccountPhoneSignup: string;
    currentEmail: string;
    emailConfirmationSecurityDescription: string;
    emailConfirmationSent: string;
    emailChangeStartFailed: string;
    sending: string;
    sendConfirmationEmail: string;
    phone: string;
    noPhoneNumber: string;
    notifications: string;
    notificationPreferences: string;
    notificationPreferencesMissing: string;
    notificationPreferencesNotSpecified: string;
    notificationPreferencesDescription: string;
    notificationsEnabled: string;
    notificationsDisabled: string;
    notificationsDisabledWarningPatient: string;
    notificationsDisabledWarningClinician: string;
    notificationPreferencesSaved: string;
    notificationPreferencesSaveFailed: string;
    appearance: string;
    darkMode: string;
    darkModeDescription: string;
    switchToDarkMode: string;
    switchToLightMode: string;
    language: string;
    languageDescription: string;
    chooseLanguage: string;
    otherActions: string;
    billingAndSubscription: string;
    hospitalDetails: string;
    clinicianNumberLabel: string;
    clinicianNumberDescription: string;
    noClinicianNumber: string;
    myOrganisations: string;
    myTeam: string;
    teamPageDescription: string;
    secretaryDefaultHelpTitle: string;
    secretaryDefaultHelp: string;
    secretaryDefaultColumnHelp: string;
    filterRole: string;
    filterOrganisation: string;
    filterAddedBy: string;
    filterAllRoles: string;
    filterAllOrganisations: string;
    filterAllAddedBy: string;
    filterInvitedByMe: string;
    filterNotInvitedByMe: string;
    filterFavouritesOnly: string;
    filters: string;
    filterTeamAria: string;
    teamFilterTitle: string;
    teamFilterDescription: string;
    selectRoles: string;
    selectOrganisations: string;
    columnsButton: string;
    columnName: string;
    columnRole: string;
    columnOrganisation: string;
    columnAddedBy: string;
    columnDefaultSecretary: string;
    columnFavourite: string;
    invitedByYou: string;
    addUser: string;
    membersShown: (count: number) => string;
    defaultForOrganisation: (organisationName: string) => string;
    setAsDefaultForOrganisation: (organisationName: string) => string;
    viewAll: string;
    teamLabel: string;
    staffCount: (count: number) => string;
    /** Organisation member count with plural handling, e.g. "1 member" / "3 members". */
    memberCount: (count: number) => string;
    favouritesCount: (count: number) => string;
    moreTeamMembers: (count: number) => string;
    noTeamMembersYet: string;
    searchTeamPlaceholder: string;
    searchTeamAria: string;
    closeSearch: string;
    openSearch: string;
    allStaff: string;
    invitedTab: string;
    favouritesTab: string;
    noTeamMembersFound: string;
    noFavouritesYet: string;
    noInvitesYet: string;
    inviteColleaguesToBuildTeam: string;
    manageTeam: string;
    secretaryOrganisationsLabel: string;
    secretaryDefaultBadge: string;
    setAsDefaultSecretary: string;
    clearDefaultSecretary: string;
    addAsMySecretary: string;
    removeAsMySecretary: string;
    addSecretaryForOrganisation: string;
    inviteNewSecretary: string;
    chooseExistingSecretary: string;
    chooseExistingSecretaryTitle: string;
    noOtherSecretariesInOrganisation: string;
    inviteUser: string;
    diaryUsageUsedLabel: string;
    diaryUsageUnlimited: string;
    diaryUsageAtCapHint: string;
    changePassword: string;
    logout: string;
    help: string;
    menu: string;
    close: string;
    contactSupportPrompt: string;
    contactSupport: string;
    incompleteTitle: string;
    incompleteMessage: string;
    profileCompletionLabel: (progress: number) => string;
    addNextMissingField: string;
    addMissingName: string;
    addMissingDateOfBirth: string;
    addMissingEmail: string;
    addMissingPhone: string;
    addMissingOrganisation: string;
    addMissingNotifications: string;
    navProfileDescription: string;
    navDiaryLabel: string;
    navDiaryDescription: string;
    goToDiary: string;
    addDoneSeeProgress: string;
    goToDiaries: string;
    /** Profile journey card when caught up with a single diary. */
    seeResults: string;
    /** Profile journey card when the patient has more than one open diary. */
    seeDiaries: string;
    onboarding: string;
    onboardingNameTitle: string;
    onboardingNameDescription: string;
    onboardingDateOfBirthTitle: string;
    onboardingDateOfBirthDescription: string;
    onboardingEmailTitle: string;
    onboardingEmailDescription: string;
    onboardingPhoneTitle: string;
    onboardingPhoneDescription: string;
    onboardingNotificationsTitle: string;
    beginOnboarding: string;
    onboardingSignedOutDescription: string;
    continue: string;
    next: string;
    save: string;
    cancel: string;
    skip: string;
    skipConfirmTitle: string;
    skipEmailDescription: string;
    skipSmsDescription: string;
    saving: string;
    creating: string;
    organisation: string;
    organisationMissing: string;
    addOrganisation: string;
    joinOrganisation: string;
    organisationMissingDescription: string;
    addOrganisationToContinue: string;
    informationMissing: string;
    role: string;
    openMenu: string;
    showUsers: string;
    showQrCode: string;
    organisationCode: string;
    organisationCodeDescriptionPrefix: string;
    requestCode: string;
    delete: string;
    deleting: string;
    rolePatient: string;
    roleClinician: string;
    roleSecretary: string;
    roleAdmin: string;
    roleManager: string;
    roleValidator: string;
    organisationDescription: string;
    profileOrganisationDescription: string;
    yourOrganisation: string;
    professional: string;
    medicalSpecialty: string;
    selectSpecialty: string;
    specialtyDescription: string;
    professionalRegistrationNumber: string;
    professionalRegistrationPlaceholder: string;
    professionalRegistrationDescription: string;
    welcomeTitle: string;
    welcomePatientDescription: string;
    welcomeClinicianDescription: string;
    welcomeSecretaryDescription: string;
    welcomeManagerDescription: string;
    settingUpAccount: string;
    gettingAccountReady: string;
    continueToProfileSetup: string;
    processingNewUser: string;
    onboardingComplete: string;
    authenticationError: string;
    setupMoment: string;
    settingUpDiary: string;
    almostThere: string;
    patientSetupCompleteDescription: string;
    setupCompleteDescription: string;
    supportMessage: string;
    retry: string;
    edit: string;
    current: string;
    change: string;
    deleteAccount: string;
    deleteAccountConfirmTitle: string;
    deleteAccountConfirmDescription: string;
    deleteAccountPageDescription: string;
    deleteAccountReason: string;
    deleteAccountSubmit: string;
    submitting: string;
    deleteAccountConfirmationLabel: string;
    deleteAccountPlaceholder: string;
    deletingAccount: string;
    warningNotificationsDisabled: string;
    editPreferences: string;
    remindersDescription: string;
    receiveEmailReminders: string;
    receiveSmsReminders: string;
    pushNotificationSettingsError: string;
    actionRequired: (count: number) => string;
    actionRequiredForms: (count: number) => string;
    diaryCardDescription: string;
    formDue: (count: number) => string;
    diariesNeedAttention: (count: number) => string;
    welcomeModalTitle: (name: string) => string;
    welcomeDescription: string;
    welcomeTestPatientTitle: string;
    welcomeTestPatientDescription: string;
    welcomeAddFirstPatient: string;
    welcomeDoctorFallback: string;
    closeBanner: string;
    home: string;
    currentPlan: string;
    currentPlanDescription: string;
    noActiveSubscription: string;
    billingStatus: string;
    nextBillingDate: string;
    accessUntil: string;
    planDetails: string;
    trialPlanDetails: string;
    enterprisePlanDetails: string;
    choosePlanToStart: string;
    upgradeYourPlan: string;
    changeYourPlan: string;
    availablePlans: string;
    upgradePlanDescription: string;
    changePlanDescription: string;
    choosePlanDescription: string;
    preferStartFree: string;
    createFreeAccount: string;
    needMore: string;
    enterprisePlan: string;
    cancelSubscription: string;
    managePaymentMethod: string;
    undoCancellationHint: string;
    paymentRequired: string;
    paymentOverdue: string;
    subscriptionCanceling: string;
    subscriptionCancelingOn: (date: string) => string;
    subscriptionCancelingPeriod: string;
    subscriptionCancelled: string;
    subscriptionCancelledDescription: string;
    unableToCancel: string;
    unableToChangePlan: string;
    unableToOpenBillingPortal: string;
    unableToStartCheckout: string;
    failedToLoadBilling: string;
    billingStatusCanceling: string;
    billingStatusUnknown: string;
    pleaseTryAgain: string;
    planStarter: string;
    planPro: string;
    perMonth: string;
    currentPlanBadge: string;
    popularBadge: string;
    starterDiaries: string;
    proDiaries: string;
    emailSmsReminders: string;
    emailSupport: string;
    prioritySupport: string;
    currentPlanButton: string;
    subscribeToStarter: string;
    subscribeToPro: string;
    downgradeToStarter: string;
    upgradeToPro: string;
    includedInEnterprise: string;
}

export interface ProfileCopy extends ProfileCopyBase {
    titleOptions: TitleSelectOption[];
    formatTitle: (title: string | null | undefined) => string;
}

const profileCopy: Record<SupportedLocale, ProfileCopyBase> = {
    "en-GB": profileCopyEn,
    "en-US": profileCopyEnUs,
    "de-DE": profileCopyDe,
    "da-DK": profileCopyDa,
    "fr-FR": profileCopyFr,
    "it-IT": profileCopyIt,
    "es-ES": profileCopyEs,
};

function buildProfileCopy(locale: SupportedLocale): ProfileCopy {
    return {
        ...pickAppCopy(profileCopy, locale),
        titleOptions: getTitleSelectOptions(locale),
        formatTitle: (title: string | null | undefined) => formatTitleLabel(title, locale),
    };
}

const profileCopyByLocale: Record<SupportedLocale, ProfileCopy> = {
    "en-GB": buildProfileCopy("en-GB"),
    "en-US": buildProfileCopy("en-US"),
    "de-DE": buildProfileCopy("de-DE"),
    "da-DK": buildProfileCopy("da-DK"),
    "fr-FR": buildProfileCopy("fr-FR"),
    "it-IT": buildProfileCopy("it-IT"),
    "es-ES": buildProfileCopy("es-ES"),
};

export function useProfileCopy(): ProfileCopy {
    const { locale } = useAppLocale();
    return useMemo(() => pickAppCopy(profileCopyByLocale, locale), [locale]);
}

export function getProfileCopy(locale: SupportedLocale): ProfileCopy {
    return pickAppCopy(profileCopyByLocale, locale);
}

export function formatRoleLabel(role: string | null | undefined, copy: ProfileCopy): string {
    switch (role) {
        case "PATIENT":
            return copy.rolePatient;
        case "CLINICIAN":
            return copy.roleClinician;
        case "SECRETARY":
            return copy.roleSecretary;
        case "ADMIN":
            return copy.roleAdmin;
        case "MANAGER":
            return copy.roleManager;
        case "VALIDATOR":
            return copy.roleValidator;
        default:
            return role ?? "";
    }
}
